import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { API } from "@/lib/content";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { NewsletterSignup } from "@/components/site/NewsletterSignup";
import { ShareButtons } from "@/components/site/ShareButtons";

export const CATEGORY_LABELS = {
  deployment: "Deployment", relocation: "PCS & relocation", reconnecting: "Reconnecting", faith: "Faith", other: "More help",
};

const Shell = ({ children }) => (
  <div className="site bg-sage text-pine min-h-screen flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

const List = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(`${API}/resources`).then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-6 py-24 md:py-32" data-testid="resources-page">
        <p className="lb text-brass-ink fu">Free resources</p>
        <h1 className="dp mt-6"><span className="ln"><span>Guides for</span></span><span className="ln"><span><em>every season.</em></span></span></h1>
        <p className="mt-6 max-w-lg text-moss fu s1">Free help for deployment, PCS moves and reconnecting at home.</p>
        <div className="mt-8 max-w-xl fu s2"><NewsletterSignup testId="resources-newsletter" /></div>
        <div className="mt-14 grid md:grid-cols-3 gap-6" data-testid="resources-list">
          {items?.map((r, i) => (
            <Reveal key={r.id} delay={i * 90}>
              <Link to={`/resources/${r.slug}`} className="group block h-full bg-paper rounded-2xl p-8 transition-transform hover:-translate-y-1" data-testid={`resource-card-${r.slug}`}>
                <p className="lb text-brass-ink">{CATEGORY_LABELS[r.category]}</p>
                <h2 className="dp !text-3xl mt-4">{r.title}</h2>
                <p className="mt-3 text-moss">{r.summary}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm">Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          ))}
          {items && !items.length && (
            <p className="md:col-span-3 rounded-2xl border border-dashed border-pine/30 p-10 text-moss" data-testid="resources-empty">New guides are on the way. In the meantime, book a time to talk with us.</p>
          )}
        </div>
      </section>
    </Shell>
  );
};

const Detail = ({ slug }) => {
  const [doc, setDoc] = useState(undefined);
  useEffect(() => { axios.get(`${API}/resources/${slug}`).then(({ data }) => setDoc(data)).catch(() => setDoc(null)); }, [slug]);
  return (
    <Shell>
      <article className="max-w-3xl mx-auto px-6 py-24 md:py-32" data-testid="resource-detail">
        <Link to="/resources" className="inline-flex items-center gap-2 text-sm underline underline-offset-4 decoration-brass" data-testid="resource-back-link"><ArrowLeft size={14} /> All guides</Link>
        {doc === null && <p className="mt-10 text-moss" data-testid="resource-not-found">This guide isn't available.</p>}
        {doc && (
          <>
            <p className="lb text-brass-ink mt-10">{CATEGORY_LABELS[doc.category]}</p>
            <h1 className="dp mt-4 !text-5xl" data-testid="resource-title">{doc.title}</h1>
            <p className="mt-4 text-moss text-lg">{doc.summary}</p>
            <div className="mt-10 space-y-5" data-testid="resource-body">
              {doc.body.split(/\n{2,}/).map((block) => {
                const [first, ...rest] = block.split("\n");
                return rest.length
                  ? <section key={block}><h2 className="dp !text-2xl">{first}</h2><p className="mt-2 whitespace-pre-line">{rest.join("\n")}</p></section>
                  : <p key={block}>{first}</p>;
              })}
            </div>
            {doc.link && <a href={doc.link} target="_blank" rel="noopener noreferrer" className="mt-8 inline-block underline underline-offset-4 decoration-brass" data-testid="resource-external-link">Learn more</a>}
            <div className="mt-12 pt-8 border-t border-pine/15">
              <ShareButtons title={`${doc.title} | Military Marriages Matter`} message={`A free guide that might help: ${doc.title}`} path={`/resources/${doc.slug}`} testId="resource-share" />
            </div>
            <div className="mt-12 flex flex-wrap gap-6 items-center">
              <a href="/#book" className="bg-pine text-sage rounded-full px-7 py-4" data-testid="resource-book-button">Book a time slot</a>
              <a href="/#prayer" className="underline underline-offset-4 decoration-brass" data-testid="resource-prayer-link">Send a prayer request</a>
            </div>
          </>
        )}
      </article>
    </Shell>
  );
};

export default function Resources() {
  const { slug } = useParams();
  return slug ? <Detail slug={slug} /> : <List />;
}
